import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import type { MediaFile } from './requests';
import { StrapiComponentType } from './useAdLoopReducer';
import WeatherTile from '../Weather';
import NewsTile from '../News';

// How long to wait for the first frame after setting a new src.
// Must be long enough for GeckoRemoteCodec to fail OMX, fall back to c2.android.avc.decoder,
// and for the software decoder to fully initialize — which takes up to ~12s on this TV.
const INITIAL_LOAD_TIMEOUT_MS = 20000;
// How long to tolerate a mid-playback stall (rebuffering) before advancing.
const BUFFER_STALL_TIMEOUT_MS = 10000;

// ── StaticImage ───────────────────────────────────────────────────────────────
// Keyed by adKey in AdPlayer so it always remounts for each new image ad,
// giving the timer a fresh closure and preventing the loop from stalling.

type StaticImageProps = {
  imageUrl: string;
  duration: number;
  onEnded(): void;
  onError(err?: unknown): void;
};

const StaticImage = ({
  imageUrl, duration, onEnded, onError,
}: StaticImageProps): ReactElement => {
  useEffect(() => {
    const id = setTimeout(onEnded, (duration || 10) * 1000);
    return () => clearTimeout(id);
  // onEnded is stable per mount because the parent keys this component by adKey.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <img
        src={imageUrl}
        width='100%'
        alt='Sponsored Content'
        style={{ borderRadius: '5px', height: 'auto', display: 'block' }}
        onError={onError}
      />
    </div>
  );
};

// ── NoMedia ───────────────────────────────────────────────────────────────────
// Rendered when blobURL is absent — immediately signals an error so the loop advances.

const NoMedia = ({ onError }: { onError(err?: unknown): void }) => {
  useEffect(() => { onError('no-media'); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <></>;
};

// ── AdPlayer ──────────────────────────────────────────────────────────────────

type AdPlayerProps = {
  mediaFile: MediaFile;
  duration: number;
  onEnded(): void;
  onError(error?: unknown): void;
  componentProps: { componentType: StrapiComponentType | null; component: unknown };
  /** Unique id of the current ad — drives the video effect when the URL is unchanged. */
  adKey?: string;
};

function AdPlayer({
  mediaFile, duration = 15, onEnded, onError, componentProps, adKey,
}: AdPlayerProps): ReactElement {
  const initialLoadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Always-current callback refs — prevents stale closures inside the video effect.
  const onEndedRef = useRef(onEnded);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onEndedRef.current = onEnded;
    onErrorRef.current = onError;
  });

  const { componentType } = componentProps;
  // eslint-disable-next-line no-underscore-dangle
  const isImage = Boolean(mediaFile?._type?.includes('image'));
  const isVideo = Boolean(mediaFile?.blobURL && !isImage && !componentType);

  const clearInitialLoadTimer = () => {
    if (initialLoadTimerRef.current !== null) clearTimeout(initialLoadTimerRef.current);
    initialLoadTimerRef.current = null;
  };

  const clearStallTimer = () => {
    if (stallTimerRef.current !== null) clearTimeout(stallTimerRef.current);
    stallTimerRef.current = null;
  };

  const startStallTimer = () => {
    clearStallTimer();
    stallTimerRef.current = setTimeout(() => onErrorRef.current('stall-timeout'), BUFFER_STALL_TIMEOUT_MS);
  };

  // React 17 does not apply the `muted` prop to the DOM element — set it imperatively.
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = true;
  }, []);

  // Persistent video element — swaps src without remounting to avoid decoder
  // re-init flashes on GeckoView/TV hardware.
  useEffect(() => {
    if (!isVideo) return undefined;

    const video = videoRef.current;
    const url = mediaFile?.blobURL;
    if (!video || !url) return undefined;

    const handleEnded = () => { clearInitialLoadTimer(); clearStallTimer(); onEndedRef.current(); };
    const handleError = () => { clearInitialLoadTimer(); clearStallTimer(); onErrorRef.current(); };
    // Restart the mid-play stall timer whenever the video rebuffers.
    const handleWaiting = () => startStallTimer();
    // First frame rendered — clear the initial-load deadline and any stall timer.
    const handlePlaying = () => { clearInitialLoadTimer(); clearStallTimer(); };

    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('stalled', handleWaiting);
    video.addEventListener('playing', handlePlaying);

    // Release the old decoder first, then load the new src only after GeckoView
    // fires `emptied` — which confirms the old MediaCodec instance has been freed.
    // Loading new src in the same JS tick as removeAttribute batches the IPC calls,
    // causing GeckoView to hold the old OMX slot while acquiring a new one. With only
    // 4 OMX slots on this MediaTek TV, 4 rapid ad transitions exhaust the pool.
    let loadDelayId: ReturnType<typeof setTimeout> | null = null;

    const startNewVideo = () => {
      try {
        video.src = url; // eslint-disable-line no-param-reassign
        video.load();
        video.play()?.catch(() => {});
      } catch { /* error handler will advance the loop */ }
      clearInitialLoadTimer();
      initialLoadTimerRef.current = setTimeout(
        () => onErrorRef.current('initial-load-timeout'),
        INITIAL_LOAD_TIMEOUT_MS,
      );
    };

    const handleEmptied = () => {
      video.removeEventListener('emptied', handleEmptied);
      if (loadDelayId !== null) { clearTimeout(loadDelayId); loadDelayId = null; }
      startNewVideo();
    };

    video.addEventListener('emptied', handleEmptied);
    // Fallback: if `emptied` never fires, proceed after 500 ms.
    loadDelayId = setTimeout(() => {
      video.removeEventListener('emptied', handleEmptied);
      loadDelayId = null;
      startNewVideo();
    }, 500);

    try {
      video.pause();
      video.removeAttribute('src'); // eslint-disable-line no-param-reassign
      video.load(); // triggers `emptied` once GeckoView clears the media element
    } catch { /* ignore */ }

    return () => {
      video.removeEventListener('emptied', handleEmptied);
      if (loadDelayId !== null) { clearTimeout(loadDelayId); loadDelayId = null; }
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('stalled', handleWaiting);
      video.removeEventListener('playing', handlePlaying);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideo, mediaFile?.blobURL, adKey]);

  // Release decoder on unmount.
  useEffect(() => () => {
    clearInitialLoadTimer();
    clearStallTimer();
    const video = videoRef.current;
    if (video) {
      try { video.pause(); video.removeAttribute('src'); video.load(); } catch { /* ignore */ }
    }
  }, []);

  if (componentType === StrapiComponentType.Weather) {
    return <WeatherTile onEnded={onEnded} weather={componentProps.component} />;
  }

  if (componentType === StrapiComponentType.News) {
    return <NewsTile onEnded={onEnded} news={componentProps.component} />;
  }

  if (!mediaFile?.blobURL) {
    return <NoMedia onError={onError} />;
  }

  if (isImage) {
    return (
      <StaticImage
        key={adKey}
        imageUrl={mediaFile.blobURL}
        duration={duration}
        onEnded={onEnded}
        onError={onError}
      />
    );
  }

  return (
    <video
      ref={videoRef}
      className='adVideoPlayer'
      muted
      autoPlay
      playsInline
      controls={false}
      poster='data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        background: 'transparent',
      }}
    />
  );
}

export default AdPlayer;
